import React, { useState, useEffect } from 'react';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import './index.scss';
import NotFound from '$/pages/not-found';
import NoDataFound from '$/components/global/noData';
import { CloDealService } from '$/services';
import Loading from '$/components/loading/Loading';
import CloSummary from '../CloSummary';
import { getIsCurrentUserAdminState, getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';

interface ICloCompsProps {
  isLoading: boolean;
  featureFlags?: string[];
  currentClo?: Bixby.Models.CloDeal;
  heatMapActive: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const CloComps: React.FC<ICloCompsProps> = props => {
  const { featureFlags, currentClo, heatMapActive, isSuperAdmin, isAdmin } = props;
  const [isLoading, setIsLoading] = useState(null as boolean | null);
  const [dataForGrid, setDataForGrid] = useState([] as Bixby.Models.CloAnalytics[]);
  const [dataError, setDataError] = useState(undefined as string | undefined);
  const [dataRetrievalComplete, setDataRetrievalComplete] = useState(false as boolean);
  const [pinnedRow, setPinnedRow] = useState([] as Bixby.Models.CloAnalytics[]);

  useEffect(() => {
    async function getComps() {
      setIsLoading(true);
      try {
        let result: Bixby.Models.CloAnalytics[] = await CloDealService.getSummaryAnalyticsByDate(
          currentClo?.closingDate ?? undefined
        );
        if (isAdmin || isSuperAdmin) {
          result = result.filter(c => c.bixbyCloId === '');
        }
        const currentCloAnalytics = result.filter(x => x.name[0].includes(currentClo?.name ?? ''));
        if (currentCloAnalytics) {
          result = result.filter(x => !x.name[0].includes(currentClo?.name ?? ''));
          setPinnedRow(currentCloAnalytics);
        }
        setDataForGrid(result || []);
      } catch (e: any) {
        setDataError('Error retrieving analytics');
      } finally {
        setIsLoading(false);
        setDataRetrievalComplete(true);
      }
    }

    if (featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
      getComps();
    }
  }, [currentClo?.closingDate, currentClo?.name, featureFlags, isAdmin, isSuperAdmin]);

  if (!featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
    return <NotFound />;
  }
  if (!Bixby.Utils.isNullOrEmpty(dataError)) {
    return (
      <React.Fragment>
        <section className="analytics-section">
          <div className="analytics-error-section">
            <p className="error">{dataError}</p>
          </div>
        </section>
      </React.Fragment>
    );
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <React.Fragment>
        <section className="clo_comps">
          {pinnedRow && currentClo && <CloSummary dataForGrid={pinnedRow} heatMapActive={heatMapActive} />}
        </section>
        {dataForGrid && !Bixby.Utils.isNullOrEmpty(dataForGrid) ? (
          <section className="clo_comps">
            <div className="top_wrap">
              <h2 className="heading_title">Comparables, By Vintage</h2>
            </div>
            <div className="sub_heading_title">
              Note: comps shown by vintage include any CLO closed within 90 days of {currentClo?.name}
            </div>
            {dataForGrid && currentClo && (
              <div className="comps_by_vintage">
                <CloSummary dataForGrid={dataForGrid} heatMapActive={heatMapActive} />
              </div>
            )}
            <p className="analytics-note">(1) Calculated as Cash + Available Line of Credit divided by Total Debt.</p>
            <p className="analytics-note">
              (2) Calculated as current 1st Lien (Total) Net Leverage divided by TEV / Adj. EBITDA at the time of
              transaction.
            </p>
            <p className="analytics-note">
              (3) A negative Dividends as % of initial equity contribution ratio reflects subsequent net equity
              contributions.
            </p>
            <p className="analytics-note">Source: Fitch-Bixby contributors and Trepp, Inc.</p>
          </section>
        ) : dataRetrievalComplete ? (
          <section className="clo_comps">
            <div className="assets_no_data">
              <NoDataFound dataType="comps" />
            </div>
          </section>
        ) : null}
      </React.Fragment>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  return {
    isLoading: state.cloDealState.isLoading,
    currentClo: state.cloDealState.currentCloDeal,
    featureFlags: state.loginState.featureFlags,
    heatMapActive: state.cloDealState.heatMapActive,
    isSuperAdmin,
    isAdmin,
  };
};

export default connect(mapStateToProps)(CloComps);
